import { useMemo } from 'react';

import { SelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { BalanceSheetCategoryType } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import { HIDDEN_BALANCE_SHEET_CATEGORY_TYPES } from '../assetCategories.constants';
import { useBalanceSheetCategoriesQuery } from '../graphql/AssetCategories.generated';

interface UseBalanceSheetCategoriesForDropdownOptions {
  /**
   * When true, returns only the Liabilities balance sheet category.
   * When false (default), returns all categories except Liabilities.
   * Used for the Liabilities asset class which maps to the Liabilities balance sheet category.
   */
  includeLiabilities?: boolean;
  /**
   * When true, returns only the Receivables balance sheet category.
   * When false (default), returns all categories except Receivables.
   * Used for the Receivables asset class which maps to the Receivables balance sheet category.
   */
  includeReceivables?: boolean;
}

export function useBalanceSheetCategoriesForDropdown(
  options?: UseBalanceSheetCategoriesForDropdownOptions
) {
  const { includeLiabilities = false, includeReceivables = false } =
    options ?? {};

  const { data, loading, error } = useBalanceSheetCategoriesQuery({
    fetchPolicy: 'cache-and-network',
  });

  const finalOptions: SelectInputOption<string>[] = useMemo(() => {
    const categories = getNodes(data?.balanceSheetCategories);

    // Filter based on whether we want Liabilities/Receivables or everything else
    const filtered = categories.filter((category) => {
      if (includeLiabilities) {
        return category.type === BalanceSheetCategoryType.Liabilities;
      }
      if (includeReceivables) {
        return category.type === BalanceSheetCategoryType.Receivables;
      }
      // Default: exclude both Liabilities and Receivables
      return !HIDDEN_BALANCE_SHEET_CATEGORY_TYPES.has(category.type);
    });

    return filtered.map((category) => ({
      display: category.displayName,
      value: category.id,
    }));
  }, [data, includeLiabilities, includeReceivables]);

  return {
    options: finalOptions,
    loading,
    error,
  };
}
